import { 
  Component, 
  OnInit, 
  OnDestroy,
  inject, 
  DestroyRef, 
  ChangeDetectionStrategy, 
  ChangeDetectorRef,
  HostListener,
  AfterViewInit,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { MaterialModule } from '../../../../shared/material/material.module';
import { UpgradeOptionsService } from '../services/upgrade-options.service';
import { 
  UpgradeOptionsResponseDTO, 
  UpgradeOptionDTO 
} from '../../../../core/generated';

/**
 * UpgradeOptionsModalComponent
 * 
 * Componente modal para visualização e seleção de opções de upgrade de planos de acesso.
 * Integra-se com o endpoint backend GET /access-plans/{planId}/upgrade-options.
 * Aberto via MatDialog com data: { planId: number }.
 */
@Component({
  selector: 'app-upgrade-options-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './upgrade-options-modal.component.html',
  styleUrl: './upgrade-options-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpgradeOptionsModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private upgradeOptionsService = inject(UpgradeOptionsService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private focusTrapFactory = inject(FocusTrapFactory);
  private dialogRef = inject(MatDialogRef<UpgradeOptionsModalComponent>);
  private data = inject<{ planId: number }>(MAT_DIALOG_DATA);

  private focusTrap?: FocusTrap;

  /** ID do plano atual, recebido via MAT_DIALOG_DATA */
  readonly planId: number = this.data.planId;

  /** Dados das opções de upgrade retornados pelo backend */
  upgradeOptions: UpgradeOptionsResponseDTO | null = null;

  /** Indica se há uma requisição em andamento */
  loading = false;

  /** Mensagem de erro, se houver */
  error: string | null = null;

  ngOnInit(): void {
    if (!this.validatePlanId()) {
      this.error = 'ID de plano inválido';
      this.cdr.markForCheck();
      return;
    }

    this.loadUpgradeOptions();
  }

  ngAfterViewInit(): void {
    const modalElement = this.elementRef.nativeElement.querySelector('.modal');
    if (modalElement) {
      this.focusTrap = this.focusTrapFactory.create(modalElement);
      this.focusTrap.focusInitialElementWhenReady();
    }
  }

  ngOnDestroy(): void {
    this.focusTrap?.destroy();
  }

  private validatePlanId(): boolean {
    return typeof this.planId === 'number' && 
           this.planId > 0 && 
           Number.isInteger(this.planId);
  }

  loadUpgradeOptions(): void {
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    this.upgradeOptionsService.getUpgradeOptions(this.planId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.upgradeOptions = data;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open(err.message, 'Fechar', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Seleciona uma opção de upgrade e fecha o modal passando a seleção como resultado.
   */
  selectUpgrade(option: UpgradeOptionDTO): void {
    if (!option.packageId) {
      this.snackBar.open('Opção de upgrade inválida', 'Fechar', { duration: 3000 });
      return;
    }

    this.dialogRef.close({ planId: this.planId, packageId: option.packageId });
  }

  /** Fecha o modal sem selecionar nenhuma opção. */
  close(): void {
    this.dialogRef.close();
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event): void {
    event.preventDefault();
    this.close();
  }

  /** Função trackBy para otimização do ngFor */
  trackByPackageId(index: number, option: UpgradeOptionDTO): number {
    return option.packageId || index;
  }
}
